import { CartItem } from '@pedix-workspace/utils';
import {
  endOrderCartItemToOrderItem,
  getCartItemsByCategory,
  sortAsscending,
} from '../cart-item/cart-item-grouping';
import { getCartItemSubtotal } from '../cart-item/cart-item-utils';
import {
  CartView,
  CartViewGroup,
  CartViewGrouping,
  CartViewProductGroup,
  CartViewSection,
} from './cart-view.types';

export function cartItemsToCartView(cartItems: CartItem[]): CartView {
  const cartItemsByCategory = getCartItemsByCategory(cartItems);
  const categoryNames = Object.keys(cartItemsByCategory);

  return {
    categories: categoryNames.map(categoryName => {
      const itemsByGroup = cartItemsByCategory[categoryName];
      const groupNames = Object.keys(itemsByGroup);

      groupNames.sort(sortAsscending);

      const groups: CartViewGroup[] = groupNames.map(groupName => ({
        name: groupName === 'undefined' ? undefined : groupName,
        isCustomGroup: groupName !== 'undefined',
        productGroups: cartItemsToProductGroups(itemsByGroup[groupName]),
      }));
      const categoryId = itemsByGroup[groupNames[0]][0].category.id;

      return {
        categoryId,
        categoryName,
        hasCustomGroups: groupNames.some(groupName => groupName !== 'undefined'),
        groups,
      };
    }),
  };
}

export function getCartViewSections(
  cartItems: CartItem[],
  grouping: CartViewGrouping,
): CartViewSection[] {
  if (grouping === 'none') {
    return cartItems.length > 0
      ? [
          {
            key: 'all',
            categoryId: null,
            header: null,
            productGroups: cartItemsToProductGroups(cartItems),
          },
        ]
      : [];
  }

  const cartView = cartItemsToCartView(cartItems);

  if (grouping === 'categories') {
    return cartView.categories.map(category => ({
      key: category.categoryName,
      categoryId: category.categoryId,
      header: { categoryName: category.categoryName, groupName: undefined, isCustomGroup: false },
      productGroups: category.groups.flatMap(group => group.productGroups),
    }));
  }

  return cartView.categories.flatMap(category =>
    category.groups.map(group => ({
      key: `${category.categoryName}:${group.isCustomGroup}:${group.name ?? ''}`,
      categoryId: category.categoryId,
      header: {
        categoryName: category.categoryName,
        groupName: group.name,
        isCustomGroup: group.isCustomGroup,
      },
      productGroups: group.productGroups,
    })),
  );
}

function cartItemsToProductGroups(cartItems: CartItem[]): CartViewProductGroup[] {
  const groupsByProductId = new Map<string, CartViewProductGroup>();

  cartItems.forEach(cartItem => {
    const productId = cartItem.product.id;
    let productGroup = groupsByProductId.get(productId);

    if (!productGroup) {
      productGroup = {
        productId,
        categoryId: cartItem.category.id,
        orderItems: [],
        subtotal: 0,
      };
      groupsByProductId.set(productId, productGroup);
    }

    productGroup.orderItems.push(endOrderCartItemToOrderItem(cartItem));
    productGroup.subtotal += getCartItemSubtotal(cartItem, { usePriceDiscount: true });
  });

  return Array.from(groupsByProductId.values());
}
